import { useRouter } from 'next/router'
import { useCallback } from 'react'

import ROUTES from '~/shared/constants/routes.const'
import { AuthenticationResponse, SuccessResult } from '~/redux/apis/types'
import { useAppDispatch } from '~/redux/hooks'

import authService from '~/services/auth.service'
import userConfig from '~/config/app'

import { LoginType } from '~/analytics/account/accountEvents.types'
import { rewardApi } from '~/redux/apis/reward-api'
import { notificationApi } from '~/redux/apis/notification-api'
import { leaderboardApi } from '~/Leaderboard/apis/leaderboard-api'

import nativeApp from '~/shared/native-app/native-app'
import { FrontendMessageType } from '~/shared/native-app/types/native-app.types'
import useGetFirstOnboardingPage from '~/onboarding/utils/useGetFirstOnboardingPage'
import { resetPetStateActionCreator } from '~/redux/slices/pet-state'
import { dragonApi } from '~/redux/apis/dragon-api'
import { setIsPreSignupActionCreator } from '~/onboarding/redux/onboarding.slice'

import useCheckSubscriptionAndBookingStatus from './useSubscriptionAndBookingStatus'

interface HandleOnLoginSuccessType {
  result: SuccessResult<AuthenticationResponse>
  loginType: LoginType
  loginAfterSignup?: boolean
  isSubscriptionCheckout?: boolean
  isSubscriptionRequired?: boolean
}

export default function useHandleOnLoginSuccess(): (config: HandleOnLoginSuccessType) => void {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const firstOnboardingPage = useGetFirstOnboardingPage()
  const { checkIfUserHasActiveTodPackage } = useCheckSubscriptionAndBookingStatus()

  const handleOnLoginSuccess = useCallback(
    (config: HandleOnLoginSuccessType) => {
      const {
        result,
        loginType,
        loginAfterSignup = false,
        isSubscriptionCheckout = false,
        isSubscriptionRequired = false,
      } = config

      const redirectFrom = (router.query.from || router.query.state) as string
      const hasRedirectFrom = redirectFrom && redirectFrom !== 'undefined'
      const isAuthenticationWithFb = loginType === LoginType.FACEBOOK

      if (result.type === 'success') {
        dispatch(rewardApi.util.invalidateTags(['Rewards']))
        dispatch(notificationApi.util.invalidateTags(['notification']))
        dispatch(leaderboardApi.util.invalidateTags(['leaderboard']))
        dispatch(dragonApi.util.invalidateTags(['dragon']))
        dispatch(resetPetStateActionCreator())
        dispatch(setIsPreSignupActionCreator(false))

        if (userConfig.isMobileApp) {
          nativeApp.SendMessage({ type: FrontendMessageType.UserId, payload: result.data.user.id.toString() })
          nativeApp.SendMessage({ type: FrontendMessageType.AccessToken, payload: result.data.accessToken })
        }
        const redirectTo = authService.getRedirectTo()
        if (redirectTo) {
          authService.setRedirectTo()
          return void router.push(redirectTo)
        }
        // NOTE:
        // the following will be replaced by above redux integration
        let redirectWithUtmParam = redirectFrom
        const utmParam = isAuthenticationWithFb ? '?utm_nooverride=1' : ''
        const utmQueryParam = isAuthenticationWithFb ? { utm_nooverride: 1 } : {}

        if (isAuthenticationWithFb && hasRedirectFrom) {
          redirectWithUtmParam = redirectFrom.includes('?')
            ? `${redirectFrom}&utm_nooverride=1`
            : `${redirectFrom}?utm_nooverride=1`
        }

        if (hasRedirectFrom && (redirectFrom.startsWith('booking') || redirectFrom.startsWith('/no/campaigns/dnb'))) {
          const pathnames: Record<string, string> = {
            'booking-for-self': ROUTES.BOOKING_FOR_SELF,
            'booking-for-other': ROUTES.BOOKING_FOR_OTHER,
            '/no/campaigns/dnb/saga-private-banking': ROUTES.DNB_CAMPAIGN_TOD,
          }

          router.push({
            // Booking and the DNB tutoring campaign 404 in the school platform. This branch still has to
            // consume the redirect rather than fall through: the `else if (hasRedirectFrom)` arm below
            // would push the bare string `booking-for-self` as a path.
            pathname: userConfig.isSchoolPlatform ? ROUTES.DASHBOARD : pathnames[redirectFrom],
            query: {
              didAuthenticate: true,
              ...utmQueryParam,
            },
          })

          return
        } else if (hasRedirectFrom && redirectFrom.includes('forgot-pin')) {
          const token = router.query?.token as string
          // /family/forgot-pin 404s in the school platform, which has no family accounts.
          if (userConfig.isSchoolPlatform) {
            router.push(ROUTES.DASHBOARD)

            return
          }
          router.push({ pathname: ROUTES.FAMILY_FORGOT_PIN, query: { token } })
        } else if (hasRedirectFrom && redirectFrom.startsWith('/bootcamp')) {
          return void router.push(redirectWithUtmParam)
        } else if ((hasRedirectFrom && redirectFrom.includes('multgame')) || router.pathname.includes('/multgame')) {
          return void router.push(`${ROUTES.MULT_GAME}${utmParam}`)
        } else if (
          (hasRedirectFrom && redirectFrom.includes('stacknload')) ||
          router.pathname.includes('/stacknload')
        ) {
          return void router.push(`${ROUTES.STACKNLOAD}${utmParam}`)
        } else if (
          (hasRedirectFrom && redirectFrom.includes('treasure-trail')) ||
          router.pathname.includes('/treasure-trail')
        ) {
          return void router.push(`${ROUTES.TREASURETRAIL}${utmParam}`)
        } else if (
          (hasRedirectFrom && redirectFrom.includes('windsurfer')) ||
          router.pathname.includes('/windsurfer')
        ) {
          return void router.push(`${ROUTES.WINDSURFER}${utmParam}`)
        } else if (
          (hasRedirectFrom && redirectFrom.includes('juniorMath')) ||
          router.pathname.includes('/juniorMath')
        ) {
          return void router.push(`${ROUTES.MATH_VAULT}${utmParam}`)
        } else if (hasRedirectFrom && redirectFrom.startsWith('/math-vault')) {
          return void router.push(redirectWithUtmParam)
        } else if (hasRedirectFrom && redirectFrom.startsWith('/campaign')) {
          return void router.push({
            pathname: redirectFrom,
            query: {
              didAuthenticate: true,
              utm_nooverride: 1,
            },
          })
        } else if (
          hasRedirectFrom &&
          redirectFrom.startsWith('/subscription/checkout/') &&
          redirectFrom.match(/signup|login/i)
        ) {
          // The whole /subscription tree 404s in the school platform, so there is no checkout to resume.
          if (userConfig.isSchoolPlatform) return router.push(ROUTES.DASHBOARD)

          const redirectToPayment = redirectFrom.replace(/signup|login/i, 'payment')

          return router.push(redirectToPayment)
        } else if (hasRedirectFrom) {
          return router.push(redirectFrom)
        } else if (isSubscriptionCheckout) {
          return
        }

        if (loginAfterSignup && !isSubscriptionRequired) {
          return router.push(firstOnboardingPage)
          // The Student Panel (/student) and family profile switcher (/family) are both 404 in the
          // school platform, so both arms fall through to the dashboard below.
        } else if (
          isSubscriptionRequired &&
          !userConfig.isSchoolPlatform &&
          checkIfUserHasActiveTodPackage(result.data.user)
        ) {
          return router.push(ROUTES.STUDENT_PANEL)
        } else if (result.data.user?.familyInfo?.isFamilyAdmin && !userConfig.isSchoolPlatform) {
          return router.push(ROUTES.FAMILY_SWITCH_PROFILE)
        } else if (result.data.user?.familyInfo?.hasLicense && !result.data.user?.familyInfo?.isFamilyAdmin) {
          return router.push(ROUTES.DASHBOARD)
        }
        router.push({ pathname: ROUTES.DASHBOARD, query: utmQueryParam })
      }
    },

    [router, dispatch, firstOnboardingPage, checkIfUserHasActiveTodPackage],
  )

  return handleOnLoginSuccess
}
